import { FC, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { Skeleton, Typography, Alert, Switch, Divider, Button, Tooltip, Modal, message, Collapse } from 'antd';
import { PlusOutlined, BellOutlined, BellFilled } from '@ant-design/icons';
import Icon from './atoms/Icon';
import { useArchive } from '../use/archive/useArchive';
import LayoutUI from './rendering/LayoutUI';
import type { ReadOnlyReason } from './rendering/readOnlyReason';
import UnmappedFieldsUI from './rendering/UnmappedFieldsUI';
import FlexBox from './atoms/FlexBox';
import FlexCol from './atoms/FlexCol';
import EntityProperties from './EntityProperties';
import { entityRecordActions, useEntityRecordScope, formStore, PageScopeContext } from '../store';
import { useSaveEntityReference } from '../use/useSaveEntityReference';
import { UserContext } from '../providers/UserContext';
import { MessageContext } from '../providers/MessageContext';

const { Text } = Typography;

interface EntityProps {
  /** Render the record's default layout (disabled) even when no record is selected, instead of the "select or create" hint. Used by embedded-entity form elements so the empty form is visible until a record is created. */
  showWhenEmpty?: boolean;
  /** Render the unmapped-fields section below the layout. Off for embedded-entity form elements. */
  showUnmappedFields?: boolean;
  /** Lock imposed by the host context (a read-only / protected form). Force-locks the record's own fields and controls regardless of the user-toggleable `entityLocked`, and is distinct from it: the editing Switch cannot re-enable editing while this is set. */
  hostLocked?: boolean;
  /** Show the toolbar "New Record" action. Off inside the Records list-detail browser, where creation lives in the list pane. */
  showCreate?: boolean;
}

export const Entity: FC<EntityProps> = ({ showWhenEmpty = false, showUnmappedFields = true, hostLocked = false, showCreate = true }) => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { archiveEntityRecord, isArchiving: archiveLoading } = useArchive();
  const {
    subscribeToEntityRecord,
    unsubscribeFromEntityRecord,
    user,
    hasEntityAccess,
    loading: authLoading,
    userProfileLoading,
  } = useContext(UserContext);
  const { handleFirestoreWriteError } = useContext(MessageContext);
  const scope = useEntityRecordScope();
  const pageScope = useContext(PageScopeContext);
  const saveEntityReference = useSaveEntityReference();
  const formSnap = useSnapshot(formStore.state);
  const insideForm = formSnap.formId !== undefined;
  const entity = scope.entity.data;
  const entityRecord = scope.entityRecord.data;
  const insideEntityContext = scope.insideEntityRecordContext;
  const entityLocked = scope.entityLocked;
  const setEntityLocked = scope.setEntityLocked;
  const entityGroupId = scope.entityGroupId;

  const disabled = !hasEntityAccess || entityLocked || hostLocked;
  /**
   * Whether the user's rights are still being established. `hasEntityAccess`
   * reads false until the user document carrying `userRights` has loaded, so
   * without this the panel accuses the reader of lacking permission for as long
   * as that read takes. Disabling meanwhile is right; saying why is not.
   */
  const rightsLoading = authLoading || userProfileLoading;
  const readOnlyReason: ReadOnlyReason | undefined =
    !rightsLoading && !hasEntityAccess ? 'noWriteAccess' : undefined;

  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const isSubscribed = useMemo(
    () => user?.uid ? entityRecord?.notificationsSubscribers?.includes(user.uid) : false,
    [user?.uid, entityRecord?.notificationsSubscribers]
  );

  const archiveLabel = useMemo(
    () => entity?.description?.shortLabel || 'This record',
    [entity?.description?.shortLabel]
  );

  const handleSubscriptionToggle = async () => {
    if (!entity?.docId || !entityRecord?.docId) return;

    setIsSubscribing(true);
    try {
      if (isSubscribed) {
        await unsubscribeFromEntityRecord(entity.docId, entityRecord.docId);
      } else {
        await subscribeToEntityRecord(entity.docId, entityRecord.docId);
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleArchiveClick = () => {
    setShowArchiveModal(true);
  };

  const handleArchiveConfirm = async () => {
    if (!entity?.docId || !entityRecord?.docId) return;

    const archived = await archiveEntityRecord(entity.docId, entityRecord.docId);
    setShowArchiveModal(false);

    if (archived) {
      messageApi.success('Record archived successfully');
      if (!insideForm) {
        navigate(`/entities/${entity.docId}`);
      }
    } else {
      messageApi.error('Failed to archive record');
    }
  };

  /**
   * `hasEntityAccess` is true on `recordsRead` alone, so a reader reaches this
   * handler with the button enabled and the rules refuse the write. The
   * rejection has to reach the user: silence here is the press doing nothing
   * (SYD-2880).
   */
  const handleAddNewRecord = async () => {
    if (!insideEntityContext) return;
    const selectRecord = pageScope.source === 'entity' ? pageScope.onSelectRecord : undefined;
    try {
      setIsCreating(true);
      const newRecordId = await entityRecordActions.createEntityRecord(scope.entityId, user?.uid || '', entityGroupId);
      selectRecord?.(newRecordId);

      if (insideForm) {
        await saveEntityReference(newRecordId, entityGroupId);
      }

      if (newRecordId && entity?.docId && !insideForm && insideEntityContext) {
        navigate(`/entities/${entity.docId}/records/${newRecordId}`);
      }
    } catch (error) {
      handleFirestoreWriteError(error, '[Entity] createEntityRecord');
    } finally {
      setIsCreating(false);
    }
  };

  if (!entity) {
    return <Skeleton active />;
  }

  if (!insideEntityContext) {
    console.error('Error: Not inside entity context.', { entity, insideEntityContext });
    return (
      <Alert
        title="Error: Missing record data."
        type="error"
        style={{
          width: '100%',
        }}
      />
    );
  }

  return (
    <FlexCol
      justifyStart
      alignStart
      style={{
        marginBottom: 5,
        width: '100%',
      }}
      noGrow
    >
      {contextHolder}
      <Modal
        title="Archive this record?"
        open={showArchiveModal}
        onCancel={() => setShowArchiveModal(false)}
        onOk={handleArchiveConfirm}
        okText="Archive"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={archiveLoading}
        centered
      >
        {archiveLabel} will be moved to archive. You can restore it later from the Archive section.
      </Modal>
      <FlexBox
        stretch
        style={{ 
          width: '100%',
          marginTop: 10
        }}
        noGrow
        justifyStart
        wrap
        role="toolbar"
        aria-label="Record controls"
      >
        {showCreate && (
          <Tooltip
            title={!hasEntityAccess ? "You don't have edit access" : ""}
            open={!hasEntityAccess ? undefined : false}
          >
            <Button
              type="link"
              size='small'
              onClick={handleAddNewRecord}
              loading={isCreating}
              icon={<PlusOutlined />}
              disabled={!hasEntityAccess || hostLocked}
            >
              New Record
            </Button>
          </Tooltip>
        )}
        <Button
          type="link"
          size='small'
          onClick={handleSubscriptionToggle}
          icon={isSubscribed ? <BellFilled /> : <BellOutlined />}
          loading={isSubscribing}
          disabled={entityRecord?.docId === null}
        >
          {isSubscribed ? 'Unsubscribe' : 'Subscribe'}
        </Button>
        <Tooltip
          title={!hasEntityAccess ? "You don't have edit access" : ""}
          open={!hasEntityAccess ? undefined : false}
        >
          <Switch
            style={{
              minWidth: 60,
            }}
            checked={!entityLocked && !hostLocked}
            onChange={(value) => setEntityLocked(!value)}
            disabled={!hasEntityAccess || hostLocked}
            checkedChildren="Editing"
            unCheckedChildren="Locked"
            aria-label="Toggle entity editing mode"
          />
        </Tooltip>
      </FlexBox>
      <EntityProperties />
      <Divider
        style={{ margin: '10px 0' }}
        />
      {(entityRecord || showWhenEmpty) && entity?.defaultUILayout && (
        <LayoutUI
          uiElement={entity.defaultUILayout.structure}
          disabled={disabled || !entityRecord}
          readOnlyReason={readOnlyReason}
        />
      )}
      {showUnmappedFields && entityRecord && entity?.defaultUILayout?.structure && (
        <UnmappedFieldsUI
          uiElement={entity.defaultUILayout.structure}
        />
      )}
      <Collapse
        size='small'
        style={{ width: '100%' }}
        bordered={false}
        items={[
          {
            key: 'archive',
            label: 'Archive',
            children:
              <FlexBox justifyStart>
                <Button
                  type="link"
                  size='small'
                  danger
                  onClick={handleArchiveClick}
                  loading={archiveLoading}
                  icon={<Icon.Archive />}
                  disabled={!hasEntityAccess || hostLocked || entityRecord?.docId === null}
                >
                  Archive
                </Button>
                <Text>
                  Move this record to archive.
                </Text>
              </FlexBox>
          }
        ]}
      />
      {!entityRecord && !showWhenEmpty && (
        <Text>
          Select or create a new record.
        </Text>
      )}
    </FlexCol>
  );
};

export default Entity;